/**
 * SaldosView -- Dashboard de SALDOS (#/saldos) ACUMULADOS por medio de pago.
 *
 * Mision: SALDOS-RELACIONAL-V1-001 B-T3 (supersede el resumen de flujo de
 * NEXO-API-SYNC-EXPAND-001 -- aquel mostraba ingresos-egresos del periodo,
 * NO el acumulado real).
 *
 * Modelo (decision Josse #1919, spec Ares #1922, backend Argy-A PR #83):
 *   Por cada MEDIO (efectivo / transferencia / ...):
 *     saldo = monto_base                  (snapshot contado a fecha_base)
 *           + entradas (venta_pagos del medio desde fecha_base)
 *           - salidas  (egresos cuyo metodo mapea al medio)
 *   La base se fija desde la UI (boton "Fijar base" por medio) cuando el dueno
 *   cuenta su efectivo/banco real -- ancla el acumulado a la realidad.
 *
 * Contrato REAL (PR #83 + fix ff1866c): GET /{id}/saldos -> porMedio[] con
 * {medio, base, fecha_base, ingresos, egresos, saldo} + egresos_sin_clasificar
 * (egresos con metodo externo/tarjeta/null: NO restan a ningun medio, paridad
 * FrmArqueoV15) + saldo_total (ya los descuenta). El client los traduce al
 * shape interno medios[]/totalSaldo/egresosSinClasificar.
 *
 * Resiliencia:
 *   - Endpoint /saldos aun no deployado (PR #83 sin mergear) -> fallback al
 *     resumen de FLUJO del periodo (getSaldos previo) + banner explicativo.
 *     Cuando el endpoint responda, la vista acumulada se enciende sola.
 *   - Sin fila en saldos_base => el server devuelve base=0 desde 1900-01-01
 *     (acumulado historico completo), siempre con efectivo+transferencia.
 *
 * @module presentation/views/SaldosView
 */

import { FinanzasApiClient } from '../../infrastructure/api/FinanzasApiClient.js';
import { getSelectedBusiness } from '../components/BusinessSelector.js';
// SALDOS-PARSER: parser colombiano compartido (3 estados: null=vacio, NaN=invalido).
import { parsearNumeroColombiano } from '../shared/numeroColombiano.js';
import { diasDesdeRespuesta, peorDia, medidoDelDia, tonoDelDia } from '../../domain/saldosDia.js';

// Deuda 4: era una copia del escape; ahora es el compartido.
import { esc as escHtml } from '../shared/texto.js';
import { regresoHtml } from '../shared/regreso.js';
import { formatearFecha } from '../shared/fechaColombiana.js';

const fmt = (n) => (Number(n) || 0).toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const hoyISO = () => new Date().toISOString().slice(0, 10);

/** Presentacion por medio conocido; default generico para medios nuevos. */
const MEDIO_META = {
  efectivo:       { label: 'Efectivo',       icon: '💵', accent: '#3fb950' },
  transferencia:  { label: 'Transferencia',  icon: '🏦', accent: '#1f6feb' },
  tarjeta:        { label: 'Tarjeta',        icon: '💳', accent: '#a371f7' },
  banco:          { label: 'Banco',          icon: '🏦', accent: '#1f6feb' },
  caja:           { label: 'Caja',           icon: '🧾', accent: '#3fb950' },
  sin_clasificar: { label: 'Sin clasificar', icon: '❓', accent: '#d29922' }
};
function medioMeta(medio) {
  const key = String(medio || '').toLowerCase();
  return MEDIO_META[key] || { label: medio || '(medio)', icon: '💰', accent: '#8b949e' };
}

export async function renderSaldos(container) {
  const state = {
    hasta: hoyISO(),
    medios: [],
    totalSaldo: 0,
    egresosSinClasificar: 0,
    mode: null // 'acumulado' | 'flujo-fallback'
  };

  container.innerHTML = `
    <section class="artifact-view saldos-view">
      <div class="artifact-header">
        <div>
          ${regresoHtml()}
          <h1>&#128202; Saldos</h1>
          <p style="margin:4px 0 0;color:#8b949e;font-size:0.875rem">
            Saldo acumulado por medio de pago &mdash; base + entradas &minus; salidas
          </p>
        </div>
        <div data-sv-poll style="font-size:0.875rem;color:#8b949e">&#8635; cargando&hellip;</div>
      </div>

      <form data-sv-filters style="display:flex;flex-wrap:wrap;gap:10px;align-items:flex-end;margin-top:14px;
                                   padding:12px;background:#161b22;border:1px solid #30363d;border-radius:8px">
        <label style="display:flex;flex-direction:column;gap:4px;font-size:0.875rem;color:#8b949e">
          Corte hasta
          <input type="date" name="hasta" class="form-input form-input-sm" value="${state.hasta}"/>
        </label>
        <button type="submit" class="btn-primary btn-sm">Aplicar</button>
        <span style="font-size:0.875rem;color:#6e7681;align-self:center">El saldo suma desde la fecha base de cada medio hasta el corte.</span>
      </form>

      <div data-sv-total style="margin-top:16px"></div>
      <div data-sv-cards style="display:grid;grid-template-columns:repeat(auto-fit,minmax(230px,1fr));gap:14px;margin-top:14px"></div>
      <div data-sv-hint style="margin-top:14px"></div>
      <div data-sv-dias style="margin-top:22px"></div>

      <div style="margin-top:18px;display:flex;gap:10px;flex-wrap:wrap">
        <a href="#/ingresos" class="btn-ghost btn-sm">Ver ingresos &rarr;</a>
        <a href="#/egresos" class="btn-ghost btn-sm">Ver egresos &rarr;</a>
        <a href="#/creditos" class="btn-ghost btn-sm">Ver creditos &rarr;</a>
        <a href="#/compras" class="btn-ghost btn-sm">Ver compras &rarr;</a>
      </div>
    </section>
  `;

  const $form  = container.querySelector('[data-sv-filters]');
  const $total = container.querySelector('[data-sv-total]');
  const $cards = container.querySelector('[data-sv-cards]');
  const $hint  = container.querySelector('[data-sv-hint]');
  const $poll  = container.querySelector('[data-sv-poll]');
  const $dias  = container.querySelector('[data-sv-dias]');

  $form.addEventListener('submit', (e) => {
    e.preventDefault();
    state.hasta = $form.querySelector('[name="hasta"]')?.value || hoyISO();
    loadSaldos();
  });

  await loadSaldos();

  async function loadSaldos() {
    const businessId = getSelectedBusiness();
    if (!businessId) {
      $total.innerHTML = '';
      $cards.innerHTML = '';
      $hint.innerHTML = `<div style="padding:18px;text-align:center;color:#d29922;background:#161b22;border:1px solid #30363d;border-radius:8px">Selecciona un negocio en la barra superior para ver sus saldos.</div>`;
      $poll.textContent = '⚠ sin negocio';
      return;
    }
    $poll.textContent = '↻ cargando…';

    const res = await FinanzasApiClient.getSaldosPorMedio(businessId, { hasta: state.hasta });
    if (res.ok) {
      state.medios = res.data.medios || [];
      state.totalSaldo = Number(res.data.totalSaldo) || 0;
      state.egresosSinClasificar = Number(res.data.egresosSinClasificar) || 0;
      state.mode = 'acumulado';
      renderAcumulado();
      loadDias(businessId);
      $poll.textContent = `✓ ${formatearFecha(new Date(), {}, { parte: 'hora' })}`;
      return;
    }

    if (res.error?.code === 'NOT_IMPLEMENTED') {
      // Endpoint /saldos aun no deployado (PR #83 review-fix) -> fallback flujo.
      await renderFlujoFallback(businessId);
      $poll.textContent = `✓ ${formatearFecha(new Date(), {}, { parte: 'hora' })} (flujo)`;
      return;
    }

    $total.innerHTML = '';
    $cards.innerHTML = '';
    $hint.innerHTML = `<div style="padding:18px;color:#f85149;background:#161b22;border:1px solid #30363d;border-radius:8px">Error: ${escHtml(JSON.stringify(res.error || {}).slice(0, 140))}</div>`;
    $poll.textContent = '⚠ error';
  }

  // ---------- SALDOS-DIA-T2: DIA POR DIA DESDE LA BASE ----------
  // Debajo de las tarjetas: una tabla por medio con la diferencia del dia y la acumulada; los faltantes en rojo y el dia
  // que mas bajo el saldo arriba. Sin el endpoint (nexo-api 395 sin servir) se dice y no se pinta nada inventado.
  const dias = { porMedio: {}, medio: null, abierto: null, fechaBase: null };

  async function loadDias(businessId) {
    $dias.innerHTML = '<p style="color:#8b949e;font-size:0.875rem">Cargando el día por día…</p>';
    const r = diasDesdeRespuesta(await FinanzasApiClient.getSaldosDiario(businessId, { hasta: state.hasta }));
    if (!r.ok) {
      $dias.innerHTML = `<div data-sv-dias-aviso style="padding:12px;color:#d29922;background:#161b22;border:1px solid #30363d;border-radius:8px;font-size:0.875rem">${
        r.code === 'NOT_IMPLEMENTED' ? 'El día por día aparece aquí cuando la API publique el saldo diario; en cuanto exista, se enciende solo.'
        : 'No pudimos traer el día por día ahora. Intenta de nuevo en un momento.'}</div>`;
      return;
    }
    dias.porMedio = r.porMedio;
    dias.fechaBase = r.fechaBase;
    const medios = Object.keys(r.porMedio);
    if (!dias.medio || !medios.includes(dias.medio)) dias.medio = medios.includes('efectivo') ? 'efectivo' : medios[0] || null;
    dias.abierto = null;
    renderDias();
  }

  function renderDias() {
    const medios = Object.keys(dias.porMedio);
    if (!medios.length) {
      $dias.innerHTML = '<p data-sv-dias-vacio style="color:#8b949e;font-size:0.875rem">Todavía no hay días desde la base para mostrar.</p>';
      return;
    }
    const filas = dias.porMedio[dias.medio] || [];
    const peor = peorDia(filas);
    const COLOR = { faltante: '#f85149', sobrante: '#d29922', cuadra: '#3fb950', 'sin-medida': '#6e7681' };
    const plata = (v) => (v === null ? '—' : `$${fmt(v)}`);
    const conSigno = (v) => (v === null ? 'sin medida' : `${v > 0 ? '+' : v < 0 ? '−' : ''}$${fmt(Math.abs(v))}`);
    $dias.innerHTML = `
      <h2 style="margin:0 0 6px;font-size:1rem;color:#f0f6fc">Día por día desde la base${dias.fechaBase ? ` <span style="color:#8b949e;font-weight:400;font-size:0.875rem">(base del ${escHtml(dias.fechaBase)})</span>` : ''}</h2>
      <div style="display:flex;gap:6px;margin-bottom:10px">
        ${medios.map((m) => `<button type="button" data-sv-dias-medio="${escHtml(m)}" aria-pressed="${m === dias.medio}" class="btn-tag-filter${m === dias.medio ? ' active' : ''}">${medioMeta(m).icon} ${escHtml(medioMeta(m).label)}</button>`).join('')}
      </div>
      ${peor ? `<div data-sv-peor style="margin-bottom:10px;padding:10px 12px;border-left:3px solid #f85149;background:#f8514914;border-radius:6px;font-size:0.875rem;color:#f0f6fc">
          El día que más bajó el saldo: <strong>${escHtml(peor.fecha)}</strong>, ${escHtml(conSigno(peor.diferenciaDia))}.</div>` : ''}
      <!-- T2 contenedores: a 375 las 7 columnas median 574 px y empujaban la PAGINA; el scroll va dentro de esta caja. -->
      <div data-sv-dias-scroll style="overflow-x:auto;max-width:100%">
      <table data-sv-dias-tabla style="width:100%;border-collapse:collapse;font-size:0.875rem">
        <thead><tr style="color:#8b949e;text-align:right">
          <th style="text-align:left;padding:6px 8px;border-bottom:1px solid #30363d">Día</th>
          <th style="padding:6px 8px;border-bottom:1px solid #30363d">Entradas</th>
          <th style="padding:6px 8px;border-bottom:1px solid #30363d">Salidas</th>
          <th style="padding:6px 8px;border-bottom:1px solid #30363d">Saldo del sistema</th>
          <th style="padding:6px 8px;border-bottom:1px solid #30363d">${dias.medio === 'efectivo' ? 'Contado (caja)' : 'Banco (extracto)'}</th>
          <th style="padding:6px 8px;border-bottom:1px solid #30363d">Diferencia del día</th>
          <th style="padding:6px 8px;border-bottom:1px solid #30363d">Acumulada</th>
        </tr></thead>
        <tbody>
          ${filas.map((d) => {
            const tono = tonoDelDia(d), med = medidoDelDia(d), abierto = dias.abierto === d.fecha;
            return `<tr data-sv-dia="${escHtml(d.fecha)}" data-tono="${tono}" style="cursor:pointer;text-align:right;border-bottom:1px solid #21262d;${tono === 'faltante' ? 'background:#f8514910' : ''}">
              <td style="text-align:left;padding:6px 8px;color:#c9d1d9">${escHtml(d.fecha)}</td>
              <td style="padding:6px 8px;color:#3fb950">${plata(d.entradas)}</td>
              <td style="padding:6px 8px;color:#f0883e">${plata(d.salidas)}</td>
              <td style="padding:6px 8px;color:#f0f6fc">${plata(d.saldoSistema)}</td>
              <td style="padding:6px 8px;color:#c9d1d9" title="${escHtml(med.texto)}">${med.valor === null ? `<span style="color:#6e7681">${escHtml(med.texto)}</span>` : plata(med.valor)}</td>
              <td style="padding:6px 8px;font-weight:600;color:${COLOR[tono]}">${escHtml(conSigno(d.diferenciaDia))}</td>
              <td style="padding:6px 8px;color:${d.diferenciaAcumulada < 0 ? '#f85149' : '#c9d1d9'}">${escHtml(conSigno(d.diferenciaAcumulada))}</td>
            </tr>${abierto ? `<tr data-sv-dia-detalle><td colspan="7" style="padding:10px 12px;background:#0d1117;font-size:0.875rem;color:#c9d1d9;text-align:left">${detalleDelDia(d)}</td></tr>` : ''}`;
          }).join('')}
        </tbody>
      </table>
      </div>`;
    $dias.querySelectorAll('[data-sv-dias-medio]').forEach((b) => b.addEventListener('click', () => { dias.medio = b.dataset.svDiasMedio; dias.abierto = null; renderDias(); }));
    $dias.querySelectorAll('[data-sv-dia]').forEach((tr) => tr.addEventListener('click', () => { dias.abierto = dias.abierto === tr.dataset.svDia ? null : tr.dataset.svDia; renderDias(); }));
  }

  // Al tocar un dia: lo que el banco no cruzo con el sistema (ventas por transferencia sin reflejo y debitos sin egreso)
  // y los pagos de Nexo de ese dia; el detalle de cada egreso vive en #/egresos.
  function detalleDelDia(d) {
    const partes = [];
    if (d.medio === 'efectivo') {
      partes.push(d.contado === null ? 'Ese día no hubo arqueo: la diferencia no se pudo medir.'
        : `Se contaron $${fmt(d.contado)} contra $${fmt(d.esperado ?? 0)} esperados (${d.arqueos ?? 1} arqueo${(d.arqueos ?? 1) === 1 ? '' : 's'}).`);
    } else if (d.noCruzo) {
      partes.push(`En el banco y no en el sistema: <strong>${d.noCruzo.creditos}</strong> entrada${d.noCruzo.creditos === 1 ? '' : 's'} por $${fmt(d.noCruzo.montoCreditos)} y <strong>${d.noCruzo.debitos}</strong> salida${d.noCruzo.debitos === 1 ? '' : 's'} por $${fmt(d.noCruzo.montoDebitos)}.`);
      if (d.nexoEntradas) partes.push(`Pagos de Nexo ese día: $${fmt(d.nexoEntradas)} (no son venta ni faltante).`);
    } else {
      partes.push('Ese día no está en el extracto cargado: la diferencia no se pudo medir.');
    }
    partes.push(`<a href="#/egresos" style="color:#58a6ff">Ver los egresos →</a>`);
    return partes.join('<br>');
  }

  // ---------- MODO ACUMULADO (endpoint relacional vivo) ----------

  function renderAcumulado() {
    // SALDOS-T1: un total SIN PUNTO DE PARTIDA no es un saldo.
    //
    // El saldo de un medio se calcula como base + entradas - salidas. Si el
    // negocio no tiene NINGUNA base fijada, lo que queda no es "cuanta plata
    // hay": es el neto de los movimientos del periodo, que es otra cosa.
    // Medido el 31-ago-2026 en Postgres: de 31 negocios, 30 NO tienen fila en
    // saldos_base. Antes esta vista les pintaba igual un "Saldo total (todos
    // los medios)", y un total sin origen se lee como plata real.
    const hayBase = state.medios.length > 0;
    const totalColor = state.totalSaldo > 0 ? '#3fb950' : state.totalSaldo < 0 ? '#f85149' : '#c9d1d9';
    const sinClasif = state.egresosSinClasificar;

    $total.innerHTML = hayBase ? `
      <div style="padding:18px;background:#161b22;border:1px solid #30363d;border-left:4px solid ${totalColor};border-radius:8px">
        <div style="font-size:0.875rem;color:#8b949e;text-transform:uppercase;letter-spacing:0.03em">&#9878;&#65039; Saldo total (todos los medios)</div>
        <div style="font-size:1.8rem;color:${totalColor};font-weight:700;margin-top:6px">$${fmt(state.totalSaldo)}</div>
        <div style="font-size:0.875rem;color:#6e7681;margin-top:4px">Corte hasta ${escHtml(state.hasta)}${sinClasif > 0 ? ` &middot; ya descuenta $${fmt(sinClasif)} sin clasificar` : ''}</div>
      </div>
      ${sinClasif > 0 ? `
      <div style="margin-top:10px;padding:10px 14px;font-size:0.875rem;color:#d29922;background:#d2992211;border:1px solid #d2992244;border-radius:6px;line-height:1.5">
        &#9888; <strong>$${fmt(sinClasif)} en egresos sin clasificar</strong> (metodo externo/tarjeta/sin dato):
        no restan al saldo de ningun medio (paridad Desktop FrmArqueoV15), pero el saldo total de arriba ya los descuenta.
      </div>` : ''}` : `
      <div style="padding:18px;background:#161b22;border:1px solid #30363d;border-left:4px solid #d29922;border-radius:8px">
        <div style="font-size:0.875rem;color:#8b949e;text-transform:uppercase;letter-spacing:0.03em">&#128200; Movimiento del periodo</div>
        <div style="font-size:1.8rem;color:#c9d1d9;font-weight:700;margin-top:6px">$${fmt(state.totalSaldo)}</div>
        <div style="font-size:0.875rem;color:#d29922;margin-top:6px;line-height:1.5">
          <strong>Esto no es tu saldo.</strong> Es lo que entro menos lo que salio hasta ${escHtml(state.hasta)}.
          Para saber cuanta plata hay, falta decir con cuanto arrancaste: cuenta el efectivo y mira el banco,
          y usa <strong>Fijar base</strong> en cada medio.
        </div>
      </div>`;

    if (state.medios.length === 0) {
      $cards.innerHTML = '';
      $hint.innerHTML = `
        <div style="padding:14px;font-size:0.875rem;color:#79c0ff;background:#1f6feb1a;border:1px dashed #1f6feb;border-radius:6px">
          &#8505; Todavia no fijaste la base de ningun medio. Cuenta tu efectivo y mira el saldo del banco, y usa <strong>Fijar base</strong>: desde ahi el panel suma entradas y resta salidas para decirte cuanta plata hay.
        </div>`;
      return;
    }

    $cards.innerHTML = state.medios.map(m => {
      const meta = medioMeta(m.medio);
      const saldoColor = m.saldo > 0 ? '#3fb950' : m.saldo < 0 ? '#f85149' : '#c9d1d9';
      const isSinClasificar = String(m.medio).toLowerCase() === 'sin_clasificar';
      return `
        <div style="padding:14px;background:#161b22;border:1px solid ${isSinClasificar ? '#d2992255' : '#30363d'};border-left:4px solid ${meta.accent};border-radius:8px">
          <div style="display:flex;justify-content:space-between;align-items:center">
            <div style="font-size:0.875rem;color:#c9d1d9;font-weight:600">${meta.icon} ${escHtml(meta.label)}</div>
            <button class="btn-ghost btn-sm" data-sv-fijar="${escHtml(m.medio)}" style="font-size:0.875rem;padding:2px 8px" title="Fijar la base contando el ${escHtml(meta.label.toLowerCase())} real">Fijar base</button>
          </div>
          <div style="font-size:1.45rem;color:${saldoColor};font-weight:700;margin-top:8px">$${fmt(m.saldo)}</div>
          <div style="font-size:0.875rem;color:#8b949e;margin-top:6px;line-height:1.6">
            Base <strong style="color:#c9d1d9">$${fmt(m.montoBase)}</strong>${m.fechaBase ? ` <span style="color:#6e7681">(${escHtml(String(m.fechaBase).slice(0, 10))})</span>` : ''}<br/>
            + Entradas <strong style="color:#3fb950">$${fmt(m.entradas)}</strong>
            &minus; Salidas <strong style="color:#f85149">$${fmt(m.salidas)}</strong>
          </div>
          ${isSinClasificar ? `<div style="margin-top:6px;font-size:0.875rem;color:#d29922">⚠ Egresos sin metodo mapeado (fix backend en curso #1929)</div>` : ''}
        </div>`;
    }).join('');

    $hint.innerHTML = `
      <div style="padding:8px 12px;font-size:0.875rem;color:#3fb950;background:#0d2818;border:1px solid #23863644;border-radius:6px">
        &#10003; Saldos acumulados en vivo (relacional): base + venta_pagos &minus; egresos por medio.
      </div>`;

    $cards.querySelectorAll('[data-sv-fijar]').forEach(btn => {
      btn.addEventListener('click', () => openFijarBaseModal(btn.dataset.svFijar));
    });
  }

  /**
   * Modal "Fijar base" -- el dueno cuenta su efectivo/banco real y ancla el
   * acumulado a esa realidad. Modal custom (regla SKILL: no prompt/confirm nativos).
   */
  function openFijarBaseModal(medio) {
    if (document.getElementById('nx-sv-base-modal')) return;
    const meta = medioMeta(medio);
    const actual = state.medios.find(m => m.medio === medio);
    const $modal = document.createElement('div');
    $modal.id = 'nx-sv-base-modal';
    $modal.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.7);z-index:1000;display:flex;align-items:center;justify-content:center;padding:16px';
    $modal.innerHTML = `
      <div style="background:#161b22;border:1px solid #30363d;border-radius:10px;padding:20px;max-width:420px;width:100%">
        <h3 style="margin:0 0 8px;color:#c9d1d9">${meta.icon} Fijar base de ${escHtml(meta.label)}</h3>
        <p style="color:#8b949e;font-size:0.875rem;margin:0 0 14px;line-height:1.5">
          Cuenta el ${escHtml(meta.label.toLowerCase())} REAL que tienes hoy y registralo.
          El saldo acumulado arranca desde este valor; las ventas y egresos posteriores se suman/restan automaticamente.
        </p>
        <div style="display:grid;grid-template-columns:110px 1fr;gap:10px;align-items:center;font-size:0.875rem">
          <label style="color:#8b949e">Fecha base</label>
          <input id="nx-sv-base-fecha" type="date" class="form-input form-input-sm" value="${hoyISO()}"/>
          <label style="color:#8b949e">Monto contado</label>
          <input id="nx-sv-base-monto" type="text" inputmode="numeric" class="form-input form-input-sm" placeholder="0" value="${actual && actual.montoBase ? actual.montoBase : ''}"/>
        </div>
        <p id="nx-sv-base-status" style="margin:10px 0 0;font-size:0.875rem;min-height:1em"></p>
        <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:14px">
          <button class="btn-ghost btn-sm" id="nx-sv-base-cancel">Cancelar</button>
          <button class="btn-primary btn-sm" id="nx-sv-base-save">Guardar base</button>
        </div>
      </div>`;
    document.body.appendChild($modal);
    const close = () => { $modal.remove(); };
    $modal.querySelector('#nx-sv-base-cancel').addEventListener('click', close);
    $modal.addEventListener('click', (e) => { if (e.target === $modal) close(); });
    $modal.querySelector('#nx-sv-base-save').addEventListener('click', async () => {
      const $btn = $modal.querySelector('#nx-sv-base-save');
      const $st = $modal.querySelector('#nx-sv-base-status');
      const fechaBase = $modal.querySelector('#nx-sv-base-fecha')?.value || hoyISO();
      // SALDOS-PARSER (luz verde #5573, hallazgo #5349): el regex artesanal
      // replace(/[^\d.-]/g,'') + Number rompia con formato colombiano — "1.500"
      // (mil quinientos) se guardaba como $1,50 (x1000 silencioso que pasaba la
      // validacion) y "500,50" perdia la coma y quedaba $50.050. Es DINERO (la
      // base del saldo acumulado): parser compartido de 3 estados, como el
      // conteo, el OCR y parseMiles.
      const montoBase = parsearNumeroColombiano($modal.querySelector('#nx-sv-base-monto')?.value);
      if (montoBase == null || !isFinite(montoBase) || montoBase < 0) {
        $st.style.color = '#f85149';
        $st.textContent = 'Monto invalido. Ingresa el valor contado (>= 0).';
        return;
      }
      $btn.disabled = true;
      $btn.textContent = 'Guardando…';
      const businessId = getSelectedBusiness();
      const res = await FinanzasApiClient.setSaldoBase(businessId, { medio, fechaBase, montoBase });
      if (res.ok) {
        close();
        loadSaldos();
      } else if (res.error?.code === 'NOT_IMPLEMENTED') {
        $btn.disabled = false;
        $btn.textContent = 'Guardar base';
        $st.style.color = '#d29922';
        $st.textContent = 'No se pudo guardar la base ahora mismo. El endpoint SI esta publicado (PUT /saldos/base, verificado el 31-ago), asi que suele ser cosa de la sesion o de la conexion: reintenta.';
      } else {
        $btn.disabled = false;
        $btn.textContent = 'Guardar base';
        $st.style.color = '#f85149';
        $st.textContent = 'Error: ' + escHtml(JSON.stringify(res.error || {}).slice(0, 120));
      }
    });
  }

  // ---------- FALLBACK FLUJO (endpoint /saldos aun no deployado) ----------

  async function renderFlujoFallback(businessId) {
    const res = await FinanzasApiClient.getSaldos(businessId, { period: 'total' });
    const t = res.ok ? (res.data?.totales || {}) : {};
    const saldoColor = (t.saldoNeto || 0) > 0 ? '#3fb950' : (t.saldoNeto || 0) < 0 ? '#f85149' : '#c9d1d9';
    $total.innerHTML = '';
    $cards.innerHTML = [
      flujoCard('💰 Ingresos', t.ingresoTotal, '#3fb950'),
      flujoCard('💸 Egresos', t.egresoTotal, '#f85149'),
      flujoCard('⚖️ Flujo neto', t.saldoNeto, saldoColor, true),
      flujoCard('🏦 Transferencias', t.transferencias, '#58a6ff'),
      flujoCard('📒 Por cobrar', t.creditosPorCobrar, '#d29922')
    ].join('');
    $hint.innerHTML = `
      <div style="padding:10px 14px;font-size:0.875rem;color:#79c0ff;background:#1f6feb1a;border:1px dashed #1f6feb;border-radius:6px;line-height:1.5">
        &#8505; <strong>Mostrando FLUJO del periodo (total), no saldo acumulado.</strong><br/>
        <span style="color:#8b949e;font-size:0.875rem">El saldo por medio se calcula como base + entradas &minus; salidas. El endpoint y la tabla YA estan publicados (verificado el 31-ago): lo que falta para ver tus saldos es fijar la base de cada medio.</span>
      </div>`;
  }

  function flujoCard(label, value, color, big = false) {
    return `
      <div style="padding:${big ? '18px' : '14px'};background:#161b22;border:1px solid #30363d;border-left:4px solid ${color};border-radius:8px">
        <div style="font-size:0.875rem;color:#8b949e;text-transform:uppercase;letter-spacing:0.03em">${label}</div>
        <div style="font-size:${big ? '1.7rem' : '1.25rem'};color:${color};font-weight:700;margin-top:6px">$${fmt(value)}</div>
      </div>`;
  }
}
